import React, { createContext, useContext, useState, useEffect } from 'react';
import { getPreferences, updatePreferences } from '../api/api';

interface FavoritesContextType {
  favorites: string[];
  toggleFavorite: (eventId: string) => void;
  isFavorite: (eventId: string) => boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<string[]>([]);
  const isLogged = !!localStorage.getItem('access_token');

  // Load from LocalStorage initially for fast render
  useEffect(() => {
    const local = localStorage.getItem('my_favorites');
    if (local) {
      try {
        setFavorites(JSON.parse(local));
      } catch (e) {}
    }
  }, []);

  // Fetch from API if logged in
  useEffect(() => {
    if (isLogged) {
      getPreferences().then(prefs => {
        if (prefs.payload && Array.isArray(prefs.payload.favorites)) {
          setFavorites(prefs.payload.favorites as string[]);
          localStorage.setItem('my_favorites', JSON.stringify(prefs.payload.favorites));
        }
      }).catch(err => console.error("Failed to fetch favorites", err));
    }
  }, [isLogged]);

  const toggleFavorite = async (eventId: string) => {
    const nextFavorites = favorites.includes(eventId)
      ? favorites.filter(id => id !== eventId)
      : [...favorites, eventId];

    setFavorites(nextFavorites);
    localStorage.setItem('my_favorites', JSON.stringify(nextFavorites));

    if (isLogged) {
      try {
        await updatePreferences({
          payload: { favorites: nextFavorites }
        });
      } catch (err) {
        console.error("Failed to update favorites to API", err);
      }
    }
  };

  const isFavorite = (eventId: string) => favorites.includes(eventId);

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite, isFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
